import { memo, useMemo, type ReactNode } from 'react'
import styled from 'styled-components'
import { Checkbox } from '@shipt/design-system-checkbox'

import {
  trackFilterUtilityViewed,
  useTrackFilterUtilityClicked,
} from '@/features/shared/analytics/filters'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { SEARCH_FILTER_TYPE, SORT_BY_FACET } from '@/features/shared/constants/global'
import {
  ScrollIntoView,
  type ScrollIntoViewProps,
} from '@/features/shared/components/ScrollIntoView'
import { FilterSection } from '@/features/shop/components/ProductFilter/FilterSection'
import { CategoryFilterSection } from '@/features/shop/components/ProductFilter/CategoryFilterSection'
import { useActiveFiltersLengths } from '@/features/shop/services/Search/hooks'
import { useValidSearchParams } from '@/features/shared/hooks/search'
import { useOnChangeFilter } from '@/features/shop/components/ProductFilter/interactions/useOnChangeFilter'
import { SortByFilterSection } from '@/features/shop/components/ProductFilter/SortByFilterSection'
import { screenSizes } from '@shipt/design-system-themes'
import { type Facet } from '@/features/shop/services/Search/types'
import { useMarketingZip } from '@/features/shop/components/UngatedCategoryPage/hooks'
import { FiltersContextProvider } from '@/features/shop/components/ProductFilter/FiltersContext'
import { findCurrentCategoryByParams } from '@/features/shop/services/Search/utils'
import { PillFilterSection } from '@/features/shop/components/ProductFilter/PillFilterSection'

type Props = {
  isCollapsible?: boolean
  hideSearchInput?: boolean
  filterToAutoExpand?: string
  facets: Facet[]
  // renders the STORE facet section. kept as a render prop so the universal
  // search dependency chain in RetailerFilterSection is only bundled by callers
  // that actually support store filtering (the ungated universal search flow).
  renderStoreSection?: (zip: string) => ReactNode
}

export const ProductFilter = memo(
  ({
    isCollapsible = false,
    hideSearchInput = false,
    filterToAutoExpand = '',
    facets,
    renderStoreSection,
  }: Props) => {
    const searchParams = useValidSearchParams()
    const activeFilterLengths = useActiveFiltersLengths(facets)
    const zip = useMarketingZip()
    const { onChangeFilter } = useOnChangeFilter()
    const trackFilterUtilityClicked = useTrackFilterUtilityClicked()

    const { ACTIVE_DEALS, BRAND, CATEGORY, SORT_BY, STORE, FLAVOR } = SEARCH_FILTER_TYPE
    const { active_deals: activeDeals, new_deals: newDeals } = searchParams

    const handleOnChangeDeals = (sectionIndex: number) => {
      const params = { active_deals: !activeDeals }
      onChangeFilter(params)

      trackFilterUtilityClicked({
        params,
        lineage: findCurrentCategoryByParams(facets, searchParams)?.lineage,
        filter_active: !activeDeals,
        filter_value: String(!activeDeals),
        list_index: sectionIndex,
        utility_type: 'filter',
        display_format: DISPLAY_FORMAT.VERTICAL,
        display_sublocation: SEARCH_FILTER_TYPE.ACTIVE_DEALS,
      })
    }

    const handleScrollIntoView = (facet: string, sectionIndex: number) => {
      trackFilterUtilityViewed({
        list_index: sectionIndex,
        filter_value: facet,
        utility_type: 'filter',
        display_format: DISPLAY_FORMAT.VERTICAL,
      })
    }

    const sortedFacets = useMemo(
      () =>
        facets.reduce<Facet[]>(
          (acc, f) => (f.facet === ACTIVE_DEALS ? [f, ...acc] : [...acc, f]),
          [SORT_BY_FACET],
        ),
      [ACTIVE_DEALS, facets],
    )

    return (
      <FiltersContextProvider facets={facets}>
        {sortedFacets.map((f, index) => {
          const { facet, name } = f
          const scrollIntoViewProps: Omit<ScrollIntoViewProps, 'children'> = {
            onInView: () => handleScrollIntoView(facet, index),
            autoSize: true,
          }
          const filterSectionProps = {
            title: name === 'Store ID' && zip ? `Retailers in ${zip}` : name,
            isCollapsible,
            hideDivider: sortedFacets.length - 1 === index,
            autoExpand: filterToAutoExpand === facet,
            activeFiltersLength: activeFilterLengths[facet] || 0,
          }
          switch (f.facet) {
            case SORT_BY:
              return (
                <ScrollIntoView {...scrollIntoViewProps} key={facet}>
                  <FilterSection {...filterSectionProps}>
                    <SortByFilterSection />
                  </FilterSection>
                </ScrollIntoView>
              )
            case CATEGORY:
              return (
                <ScrollIntoView {...scrollIntoViewProps} key={facet}>
                  <FilterSection {...filterSectionProps}>
                    <CategoryFilterSection items={f.items} />
                  </FilterSection>
                </ScrollIntoView>
              )
            case BRAND:
              return (
                <ScrollIntoView {...scrollIntoViewProps} key={facet}>
                  <FilterSection {...filterSectionProps}>
                    <PillFilterSection
                      items={f.items}
                      hideSearchInput={hideSearchInput}
                      name={BRAND}
                    />
                  </FilterSection>
                </ScrollIntoView>
              )
            case FLAVOR:
              return (
                <ScrollIntoView {...scrollIntoViewProps} key={facet}>
                  <FilterSection {...filterSectionProps}>
                    <PillFilterSection
                      items={f.items}
                      hideSearchInput={hideSearchInput}
                      name={FLAVOR}
                    />
                  </FilterSection>
                </ScrollIntoView>
              )
            case STORE:
              return (
                zip &&
                renderStoreSection && (
                  <ScrollIntoView {...scrollIntoViewProps} key={facet}>
                    <FilterSection {...filterSectionProps}>{renderStoreSection(zip)}</FilterSection>
                  </ScrollIntoView>
                )
              )
            case ACTIVE_DEALS:
              if (newDeals) return null
              return (
                <ScrollIntoView {...scrollIntoViewProps} key={facet}>
                  <ActiveDealsCheckbox
                    id="ProductFilter-deals"
                    checked={Boolean(activeDeals)}
                    onChange={() => handleOnChangeDeals(index)}
                    label="Deals"
                    name="productFilter-deals"
                  />
                </ScrollIntoView>
              )
            default:
              return null
          }
        })}
      </FiltersContextProvider>
    )
  },
)

const ActiveDealsCheckbox = styled(Checkbox)`
  margin: 0.5rem 1rem;
  padding: 4px 0;

  @media ${screenSizes.tablet} {
    margin: 1rem 0;
    padding: 0;
  }
`
